import { useFormatters } from '../useFormatters';
import { BookingsGroup } from '../../../types';
import { isCourse } from '../../../utils/bookInfo';
import { useCallback } from 'react';
import { useApplicationContext } from '../useApplicationContext';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import { ExperimentsConsts } from '../../../consts/experiments';
import { allDayCalendarDays, allDayInclusiveEndUTC } from '@wix/bookings-checkout-common-components';

export interface TitleAndSubtitleGetters {
  getTitle: (group: BookingsGroup) => string;
  getSubtitle: (group: BookingsGroup) => string;
  getSubtitleWithoutLocation: (group: BookingsGroup) => string;
}

const hasCourseBegan = (startDate: Date) => {
  return startDate <= new Date();
};

export const useGroupHeaderGetters = () => {
  const { getStartDate, getStartTime } = useFormatters();
  const { selectedTimezone } = useApplicationContext();
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const { language } = useEnvironment();
  const isAllDayEnabled = experiments.enabled(
    ExperimentsConsts.AllDayMultiDayEvents,
  );

  const getTitle = useCallback(
    (group: BookingsGroup) =>
      group.bookings.map((booking) => booking.service?.name).join(', '),
    [],
  );

  const getSubtitleWithoutLocation = useCallback(
    (group: BookingsGroup) => {
      const { bookings } = group;
      const firstBooking = bookings[0];
      const startDate = new Date(firstBooking.start);

      if (isAllDayEnabled && firstBooking.allDay) {
        const days = allDayCalendarDays(firstBooking.start, firstBooking.end);

        const utcDateFmt = new Intl.DateTimeFormat(language, {
          timeZone: 'UTC',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
        const formattedStart = utcDateFmt.format(startDate);

        if (days === 1) {
          return t(
            'app.my-bookings-widget.bookings-details.all-day.single-day',
            { date: formattedStart, days },
          );
        }

        return t(
          'app.my-bookings-widget.bookings-details.all-day.date-range',
          {
            startDate: formattedStart,
            endDate: utcDateFmt.format(allDayInclusiveEndUTC(firstBooking.end)),
            days,
          },
        );
      }

      const formattedDateAndTimeList = [];

      if (isCourse(group)) {
        const coursePrefixKey = hasCourseBegan(startDate)
          ? 'app.my-bookings-widget.bookings-details.course-started-on'
          : 'app.my-bookings-widget.bookings-details.course-starts';

        formattedDateAndTimeList.push(
          t(coursePrefixKey, { date: getStartDate(startDate) }),
        );
      } else {
        formattedDateAndTimeList.push(
          getStartDate(startDate),
          getStartTime(startDate),
        );
      }

      return formattedDateAndTimeList
        .filter((formatted) => !!formatted)
        .join(', ');
    },
    [selectedTimezone?.timezone, isAllDayEnabled, language],
  );

  const getSubtitle = useCallback(
    (group: BookingsGroup) => {
      const { bookings } = group;
      const firstBooking = bookings[0];
      const locationInfo = firstBooking.locationInfo;

      const baseSubtitle = getSubtitleWithoutLocation(group);
      const locationInfoSubtitle = locationInfo ? `• ${locationInfo}` : '';

      return `${baseSubtitle} ${locationInfoSubtitle}`;
    },
    [getSubtitleWithoutLocation, selectedTimezone?.timezone],
  );

  return { getTitle, getSubtitle, getSubtitleWithoutLocation };
};
